/**
 * Base abstract class for all collection types
 * Contains common functionality shared across different collection implementations
 */
export abstract class CollectionAbstraction {
  abstract clear(): void;

  /**
   * Freezes an object recursively, including nested objects and arrays
   * @param obj The object to freeze
   * @returns The frozen object
   */
  protected deepFreeze<T>(obj: T): T {
    // Freeze the current object or array
    Object.freeze(obj);

    // Determine how to iterate: over properties (for objects) or elements (for arrays)
    if (Array.isArray(obj)) {
      // --- Handle Arrays ---
      obj.forEach((element) => {
        // Check if the element is an object or array, not null, and not already frozen
        if (element !== null && typeof element === "object" && !Object.isFrozen(element)) {
          // Recursively freeze the nested object/array element
          this.deepFreeze(element);
        }
      });
    } else if (typeof obj === "object" && obj !== null) {
      // --- Handle Plain Objects ---
      const propNames = Reflect.ownKeys(obj);

      // Freeze properties before freezing self
      for (const name of propNames) {
        const value = obj[name as keyof T];

        if ((value && typeof value === "object") || typeof value === "function") {
          this.deepFreeze(value);
        }
      }
    }

    // Return the frozen object or array
    return obj;
  }
}
